import React from 'react';
import { DirectionTabs } from './DirectionTabs';
import { BestOnlyToggle } from './BestOnlyToggle';
import { RefreshButton } from './RefreshButton';
import { MetricsDisplay } from './MetricsDisplay';
import { RouteFilterToolbar, RouteFilterReflectorOption } from './RouteFilterToolbar';
import { ViewModeTabs, BgpViewMode } from './ViewModeTabs';
import { RouteFilters } from '../../utils/filterModel';

interface VisibilityToolbarProps {
  direction: 'INBOUND' | 'OUTBOUND';
  onDirectionChange: (direction: 'INBOUND' | 'OUTBOUND') => void;
  bestOnly: boolean;
  onBestOnlyToggle: () => void;
  onRefresh: () => void;
  loading: boolean;
  viewMode: BgpViewMode;
  onViewModeChange: (mode: BgpViewMode) => void;
  /** Metrics for the active direction + view. */
  metrics: { neighbors: number; vpcs: number; routeTables: number; prefixes: number };
  lastUpdate: Date | null;
  allSessionsDown: boolean;
  /**
   * Absent until an inventory entity is selected — there is nothing to filter before then, so
   * the filter slot collapses and the remaining controls keep their positions.
   */
  filter: {
    onChange: (next: RouteFilters) => void;
    reflectorOptions: RouteFilterReflectorOption[];
    includeReflector: boolean;
    matchedCount: number;
    totalCount: number;
  } | null;
}

/**
 * The whole BGP Visibility control bar on a single row: direction, best-only, route filter,
 * refresh, counters and the view switch. Replaces three stacked rows (tabs+view / buttons+metrics
 * / filter+count) so the vertical space goes to the canvas instead of the chrome.
 *
 * Carries no container chrome of its own — no padding, border, background or sticky positioning —
 * because it is rendered into the SideNav title row (via `SideNavTitlePortal`), which already
 * supplies all of that. Keeping it placement-agnostic is what lets it sit beside the page title
 * instead of occupying a band under it.
 *
 * Wrap rule: the interactive controls stay on one line at every width. Only the read-only metrics
 * strip drops to a second line under 1200px (`w-full` + `order-1` push it onto its own row),
 * which keeps the bar from clipping without ever hiding a control or a number.
 */
export const VisibilityToolbar: React.FC<VisibilityToolbarProps> = ({
  direction,
  onDirectionChange,
  bestOnly,
  onBestOnlyToggle,
  onRefresh,
  loading,
  viewMode,
  onViewModeChange,
  metrics,
  lastUpdate,
  allSessionsDown,
  filter
}) => {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <DirectionTabs direction={direction} onDirectionChange={onDirectionChange} />

      <span
        aria-hidden="true"
        className="h-6 w-px flex-shrink-0 bg-semantic-border-secondary"
      />

      <BestOnlyToggle bestOnly={bestOnly} onToggle={onBestOnlyToggle} />

      {/* Takes the slack so the bar stays balanced whether or not an entity is selected. */}
      <div className="min-w-[220px] flex-1">
        {filter && (
          <RouteFilterToolbar
            onChange={filter.onChange}
            reflectorOptions={filter.reflectorOptions}
            includeReflector={filter.includeReflector}
            matchedCount={filter.matchedCount}
            totalCount={filter.totalCount}
          />
        )}
      </div>

      <RefreshButton onRefresh={onRefresh} loading={loading} />

      <div className="order-1 w-full min-[1200px]:order-none min-[1200px]:w-auto">
        <MetricsDisplay
          neighbors={metrics.neighbors}
          vpcs={metrics.vpcs}
          rts={metrics.routeTables}
          prefixes={metrics.prefixes}
          direction={direction}
          lastUpdate={lastUpdate}
          allSessionsDown={allSessionsDown}
        />
      </div>

      <ViewModeTabs viewMode={viewMode} onViewModeChange={onViewModeChange} />
    </div>
  );
};
